import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import KeyIcon from "@mui/icons-material/Key";
import { api } from "../api/client";
import type { ClientUserRow } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { useMutation, useResource } from "../hooks/useResource";
import { ConfirmDialog, ErrorBanner, Mono, RoleGate, when } from "./common";
import { MONO } from "../theme";

/**
 * Portal logins for one client, on the client's own screen.
 *
 * Here rather than on a global "portal users" page because a portal login only
 * means anything relative to a client: the client id is what scopes every lead the
 * account will ever see, and the API takes it from the path for exactly that
 * reason. A flat list would need a client picker, and a client picker is a field
 * somebody can get wrong.
 *
 * The generated password is shown once, in the response to the request that made
 * it. It is never stored in plaintext, never written to the audit log, and there is
 * no endpoint that reads it back — a lost password is a reset.
 */

/** Used until `/api/meta` answers; the server's `clientRoles` wins once it has. */
const FALLBACK_ROLES = ["member", "admin"];

export function ClientUsersPanel({
  clientId,
  clientName,
  roles = FALLBACK_ROLES,
}: {
  clientId: string;
  clientName: string;
  /** From `/api/meta`, so the form cannot offer a role the API would reject. */
  roles?: string[];
}) {
  const { can } = useAuth();
  const mayManage = can("operator");
  // A viewer gets a 403 from every route here, so it is not asked. The panel says
  // why instead of rendering an error the viewer cannot act on.
  const users = useResource<{ items: ClientUserRow[] }>(
    mayManage ? `/api/clients/${clientId}/users` : null,
  );

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<ClientUserRow | null>(null);
  const [resetting, setResetting] = useState<ClientUserRow | null>(null);
  const [deactivating, setDeactivating] = useState<ClientUserRow | null>(null);
  /** A password the server just generated, held only until the dialog is closed. */
  const [revealed, setRevealed] = useState<{ email: string; password: string } | null>(null);

  const reset = useMutation(async (user: ClientUserRow) => {
    const res = await api.post<{ email: string; password: string }>(
      `/api/clients/${clientId}/users/${user.id}/reset`,
    );
    setRevealed({ email: res.email, password: res.password });
  });

  const deactivate = useMutation(async (user: ClientUserRow) => {
    await api.post(`/api/clients/${clientId}/users/${user.id}/deactivate`);
  });

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h6">Portal logins</Typography>
          <Typography variant="caption" color="text.secondary">
            Sign-ins to the client portal at <Mono>/portal</Mono>. A portal login reads this client's
            leads and reports outcomes on them, and nothing else.
          </Typography>
        </Box>
        <RoleGate allowed={mayManage} needs="operator">
          <Button startIcon={<AddIcon />} onClick={() => setAdding(true)}>
            Add login
          </Button>
        </RoleGate>
      </Stack>

      {!mayManage ? (
        <Alert severity="info">
          Portal logins are managed by an operator. Ask one to add or reset an account.
        </Alert>
      ) : null}

      <ErrorBanner error={users.error ?? reset.error ?? deactivate.error} onRetry={users.reload} />

      {mayManage && users.data && users.data.items.length === 0 ? (
        <Alert severity="info">
          No portal login yet. Leads still get delivered over the channels below — the portal is the
          place the client can read them back and tell you what happened.
        </Alert>
      ) : null}

      {mayManage && users.data && users.data.items.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Last sign-in</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.data.items.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Mono>{user.email}</Mono>
                </TableCell>
                <TableCell>{user.name ?? "—"}</TableCell>
                <TableCell>
                  <Tooltip
                    title={
                      user.role === "admin"
                        ? "The account owner at the client. No console privilege of any kind."
                        : "Reads this client's leads and reports outcomes."
                    }
                  >
                    <Chip label={user.role} variant="outlined" />
                  </Tooltip>
                </TableCell>
                <TableCell>{user.lastLoginAt ? when(user.lastLoginAt) : "never"}</TableCell>
                <TableCell>
                  <Chip
                    label={user.active ? "active" : "disabled"}
                    color={user.active ? "success" : "default"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Edit name, role or access">
                      <IconButton onClick={() => setEditing(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate a new password and sign out their sessions">
                      <IconButton onClick={() => setResetting(user)}>
                        <KeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={user.active ? "Revoke access now" : "Already disabled"}>
                      <span>
                        <IconButton
                          color="error"
                          disabled={!user.active}
                          onClick={() => setDeactivating(user)}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {adding ? (
        <AddUserDialog
          clientId={clientId}
          clientName={clientName}
          roles={roles}
          onClose={() => setAdding(false)}
          onCreated={(email, password) => {
            setAdding(false);
            setRevealed({ email, password });
            users.reload();
          }}
        />
      ) : null}

      {editing ? (
        <EditUserDialog
          clientId={clientId}
          user={editing}
          roles={roles}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            users.reload();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={resetting !== null}
        title={`Reset the password for ${resetting?.email ?? ""}?`}
        confirmLabel="Reset password"
        pending={reset.pending}
        body="A new password is generated and shown once. Every session this login currently holds is signed out immediately."
        onCancel={() => setResetting(null)}
        onConfirm={() => {
          const target = resetting;
          if (!target) return;
          void reset.run(target).then((ok) => {
            if (!ok) return;
            setResetting(null);
            users.reload();
          });
        }}
      />

      <ConfirmDialog
        open={deactivating !== null}
        title={`Disable ${deactivating?.email ?? ""}?`}
        confirmLabel="Disable login"
        pending={deactivate.pending}
        body="Access stops on their next request, not their next sign-in. The row is kept: the outcomes they reported are the scoreboard, and the audit trail refers to them by email. Re-enable from Edit."
        onCancel={() => setDeactivating(null)}
        onConfirm={() => {
          const target = deactivating;
          if (!target) return;
          void deactivate.run(target).then((ok) => {
            if (!ok) return;
            setDeactivating(null);
            users.reload();
          });
        }}
      />

      {revealed ? (
        <PasswordOnceDialog
          email={revealed.email}
          password={revealed.password}
          onClose={() => setRevealed(null)}
        />
      ) : null}
    </Paper>
  );
}

/**
 * Create a portal login.
 *
 * There is no password field, deliberately. The operator has no reason to choose a
 * client's password, and a body field would put a live credential in the request
 * log of every proxy between this tab and the database — the API refuses one.
 */
function AddUserDialog({
  clientId,
  clientName,
  roles,
  onClose,
  onCreated,
}: {
  clientId: string;
  clientName: string;
  roles: string[];
  onClose: () => void;
  onCreated: (email: string, password: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("member");

  const save = useMutation(async () => {
    const res = await api.post<{ user: ClientUserRow | null; password: string }>(
      `/api/clients/${clientId}/users`,
      {
        email: email.trim(),
        // Omitted rather than sent empty: an empty name is not a name, and the
        // body is strict about what it accepts.
        ...(name.trim() === "" ? {} : { name: name.trim() }),
        role,
      },
    );
    onCreated(res.user?.email ?? email.trim().toLowerCase(), res.password);
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add a portal login</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This login will see {clientName}'s leads and nothing else.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText="Stored lower-case. It is also their sign-in name."
            autoFocus
            fullWidth
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Optional. Only for your own reference."
            fullWidth
          />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            helperText="Both roles see the same leads today. Neither carries any console privilege."
            fullWidth
          >
            {roles.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <Alert severity="info">
            A password is generated by the server and shown to you once, on the next screen.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={email.trim() === "" || save.pending}
          onClick={() => void save.run(undefined)}
        >
          {save.pending ? "Creating…" : "Create login"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Rename, re-role or re-enable.
 *
 * A role change or a disable revokes the user's live sessions server-side, so the
 * dialog says so rather than letting an operator discover it from a support call.
 * The email is not editable: it is the sign-in name and the identity the audit
 * trail records, so changing it is a new login, not an edit.
 */
function EditUserDialog({
  clientId,
  user,
  roles,
  onClose,
  onSaved,
}: {
  clientId: string;
  user: ClientUserRow;
  roles: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(user.name ?? "");
  const [role, setRole] = useState(user.role);
  const [active, setActive] = useState(user.active);

  // A role the server has but this build's meta does not would otherwise render as
  // an empty select and be silently overwritten on save.
  const options = roles.includes(user.role) ? roles : [user.role, ...roles];
  const nextName = name.trim() === "" ? null : name.trim();
  const unchanged = nextName === (user.name ?? null) && role === user.role && active === user.active;
  const revokes = role !== user.role || (!active && user.active);

  const save = useMutation(async () => {
    await api.put(`/api/clients/${clientId}/users/${user.id}`, { name: nextName, role, active });
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit {user.email}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Blank clears it."
            fullWidth
          />
          <TextField
            select
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            fullWidth
          >
            {options.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Access"
            value={active ? "true" : "false"}
            onChange={(e) => setActive(e.target.value === "true")}
            fullWidth
          >
            <MenuItem value="true">Enabled</MenuItem>
            <MenuItem value="false">Disabled</MenuItem>
          </TextField>
          {revokes ? (
            <Alert severity="warning">
              This signs out every session the login currently holds. They will need to sign in again.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.pending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={unchanged || save.pending}
          onClick={() => void save.run(undefined).then((ok) => ok && onSaved())}
        >
          {save.pending ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The one and only sight of a generated password.
 *
 * Rendered as selectable monospace with a copy button, because the realistic
 * delivery is an operator reading it down a phone or pasting it into a message —
 * and because a password nobody managed to copy is a second reset.
 */
function PasswordOnceDialog({
  email,
  password,
  onClose,
}: {
  email: string;
  password: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    // `navigator.clipboard` is absent on an insecure origin, which a self-hosted
    // console on plain HTTP genuinely is. The password is on screen either way.
    void navigator.clipboard?.writeText(password).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Password for {email}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This is the only time it is shown. It is not stored anywhere readable and cannot be looked
          up — if it is lost, reset it.
        </Alert>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              flexGrow: 1,
              p: 1.5,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              fontFamily: MONO,
              fontSize: 16,
              userSelect: "all",
              wordBreak: "break-all",
            }}
          >
            {password}
          </Box>
          <Tooltip title={copied ? "Copied" : "Copy to clipboard"}>
            <IconButton onClick={copy}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          Send it over something the client already trusts, and tell them to change it from the
          account menu once they are in.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          I have saved it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
